import { saveAs } from 'file-saver';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import injectSheet from 'react-jss';

import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { withFlags } from '~/flags/withFlags';
import { withCurrentUser } from '~/hocs/withCurrentUser';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';

import { black2 } from '../../../assets/shared-styles/general';
import {
  MODAL_DELETE,
  MODAL_NAME,
  MODAL_VERSION_DOCUMENT_VIEWER,
} from '../../../types/modal.types';
import { ERROR, SUCCESS } from '../../../types/toast.types';
import {
  getDocumentVersionDOCX,
  getDocumentVersionOriginal,
} from '../../DocumentsViewPage/DocumentVersions/DocumentVersions.data';
import { showToast } from '../../Shared/EcToast/showToast';
import ActionsMenu from '../ActionsMenu/ActionsMenu';
import ChevronDownIcon from '../Icons/ChevronDownIcon';
import ChevronRightIcon from '../Icons/ChevronRightIcon';
import InfoIcon from '../Icons/InfoIcon';
import styles from './EcVersionItem.styles';

class EcVersionItem extends Component {
  constructor(props) {
    super(props);

    this.handleExpandRow = this.handleExpandRow.bind(this);
    this.handleFileAttachment = this.handleFileAttachment.bind(this);

    this.state = { expanded: false, fileAttached: null };
  }

  handleExpandRow() {
    this.setState({ expanded: !this.state.expanded });
  }

  handleDownloadOriginal(item) {
    const { id, file_name, file_type } = item;
    const { intl } = this.props;

    const successMessage = intl.formatMessage(
      {
        id: 'EcVersionItem.downloadSuccessMessage2',
        defaultMessage: '"{fileName}" is downloaded successfully.',
        description:
          'Success message for file download. Example: fileName = "contract.pdf"',
      },
      { fileName: file_name },
    );
    getDocumentVersionOriginal(id)
      .then((res) => {
        saveAs(res, `${file_name}${file_type}`);
        showToast(SUCCESS, successMessage);
      })
      .catch(() => showToast(ERROR, successMessage));
  }

  handleDownloadOCR(item) {
    const { id, file_name } = item;
    const { intl } = this.props;
    getDocumentVersionDOCX(id)
      .then((res) => {
        saveAs(res, `${file_name}.${res.type.split('/').pop()}`);
        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'EcVersionItem.downloadSuccessMessage',
              defaultMessage: '"{fileName}" is downloaded successfully.',
              description:
                'Success message for file download. Example: fileName = "contract.pdf"',
            },
            { fileName: file_name },
          ),
        );
      })
      .catch(() =>
        showToast(
          ERROR,
          intl.formatMessage(
            {
              id: 'EcVersionItem.downloadErrorMessage',
              defaultMessage:
                'Something went wrong with downloading "{fileName}".',
              description:
                'Error message for file download. Example: fileName = "contract.pdf"',
            },
            { fileName: file_name },
          ),
        ),
      );
  }

  handleFileAttachment() {
    const { item, handleVersionReplace } = this.props;

    handleVersionReplace(item, this.refs.fileContainer.files[0]);
  }

  getMenuItems = (item) => {
    const items = [];
    const { intl } = this.props;

    const {
      userVisibilityLevel,
      handleShowModal,
      handleVersionRestore,
      isLastVersionLeft,
    } = this.props;
    const canEdit = userVisibilityLevel === 'OPEN';
    const canDownload = canEdit || userVisibilityLevel === 'OPEN_NO_EDIT';

    if (canEdit) {
      items.push({
        content: intl.formatMessage({
          id: 'EcVersionItem.editMenuItem',
          defaultMessage: 'Edit',
          description: 'Menu item to edit version',
        }),
        onClick: () => handleShowModal(MODAL_NAME, item),
      });
      if (!item.is_latest) {
        items.push({
          content: intl.formatMessage({
            id: 'EcVersionItem.restoreMenuItem',
            defaultMessage: 'Restore',
            description: 'Menu item to restore version',
          }),
          onClick: () => handleVersionRestore(item),
        });
      }
    }

    if (canDownload) {
      const text = intl.formatMessage({
        id: 'EcVersionItem.downloadMenuItem',
        defaultMessage: 'Download',
        description: 'Menu item to download version',
      });
      items.push({
        content: text,
        onClick: () => this.handleDownloadOriginal(item),
      });
    }

    if (canEdit && !isLastVersionLeft) {
      items.push({
        content: intl.formatMessage({
          id: 'EcVersionItem.deleteMenuItem',
          defaultMessage: 'Delete',
          description: 'Menu item to delete version',
        }),
        onClick: () => handleShowModal(MODAL_DELETE, item),
      });
    }

    return items;
  };

  renderExtraOptions(item) {
    const { classes, userVisibilityLevel, intl } = this.props;
    const noEditDownload = userVisibilityLevel === 'OPEN_NO_EDIT_DOWNLOAD';

    if (noEditDownload) return null;
    return (
      <div className={classes.actionsWrapper}>
        <ActionsMenu
          id={`document_versions_row_actions_menu?id=${item.id}`}
          items={this.getMenuItems(item)}
          align="end"
          title={intl.formatMessage({
            id: 'EcVersionItem.actionsMenuTitle',
            defaultMessage: 'document versions row actions menu',
            description: 'Accessibility title for version actions menu',
          })}
        />
      </div>
    );
  }

  renderFilenameTooltip(item) {
    return (
      <Tooltip
        id={`ruleText-${item.id}`}
        placement="top"
        tooltip={item.file_name}
      >
        {item.file_name}
      </Tooltip>
    );
  }

  renderDetail(item) {
    const { classes, intl, formatDate } = this.props;

    return (
      <div className={classes.detailRowContainer}>
        <div className={classes.detailInfoHeader}>
          <InfoIcon color={black2} size="18px" />{' '}
          {intl.formatMessage({
            id: 'EcVersionItem.detailsHeader',
            defaultMessage: 'Details',
            description: 'Header for version details section',
          })}
        </div>
        <div className={classes.detailRow}>
          <div className={classes.detailItem}>
            <div className={classes.detailItemTitle}>
              {intl.formatMessage({
                id: 'EcVersionItem.createdAtLabel',
                defaultMessage: 'CREATED AT',
                description: 'Label for creation date',
              })}
            </div>
            <div className={classes.detailItemValue}>
              {formatDate(item.created_date, 'short_datetime')}
            </div>
          </div>
          <div className={classes.detailItem}>
            <div className={classes.detailItemTitle}>
              {intl.formatMessage({
                id: 'EcVersionItem.createdByLabel',
                defaultMessage: 'CREATED BY',
                description: 'Label for creator',
              })}
            </div>
            <div className={classes.detailItemValue}>{item.created_by}</div>
          </div>
          <div className={classes.detailItem}>
            <div className={classes.detailItemTitle}>
              {intl.formatMessage({
                id: 'EcVersionItem.lastModifiedAtLabel',
                defaultMessage: 'LAST MODIFIED AT',
                description: 'Label for last modification date',
              })}
            </div>
            <div className={classes.detailItemValue}>
              {formatDate(item.date_modified, 'short_datetime')}
            </div>
          </div>
          <div className={classes.detailItem}>
            <div className={classes.detailItemTitle}>
              {intl.formatMessage({
                id: 'EcVersionItem.lastModifiedByLabel',
                defaultMessage: 'LAST MODIFIED BY',
                description: 'Label for last modifier',
              })}
            </div>
            <div className={classes.detailItemValue}>
              {item.last_modified_by}
            </div>
          </div>
          <div className={classes.detailItem}>
            <div className={classes.detailItemTitle}>
              {intl.formatMessage({
                id: 'EcVersionItem.descriptionLabel',
                defaultMessage: 'DESCRIPTION',
                description: 'Label for version description',
              })}
            </div>
            <div className={classes.detailItemValue}>{item.description}</div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { classes, item, dragHandle, handleShowModal, intl, formatDate } =
      this.props;
    const { expanded } = this.state;

    return (
      <div className={classes.versionItemRowContainer}>
        <div className={classes.fieldRow}>
          <span className={classes.fieldNameContainer}>
            <span className={classes.dragHandleContainer}>{dragHandle}</span>
            <button
              aria-label={`${expanded ? 'collapse' : 'expand'} version details`}
              aria-expanded={expanded}
              className={classes.expandIcon}
              onClick={this.handleExpandRow}
              data-testid="version-expand-detail-icon"
            >
              {expanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </button>

            {item.label}
          </span>
          <span className={classes.fieldVersionContainer}>
            {item.version_tag}
          </span>
          <span className={classes.fieldFilenameContainer}>
            {this.renderFilenameTooltip(item)}
          </span>
          <span className={classes.fieldLastModContainer}>
            <FormattedMessage
              id="EcVersionItem.lastModifiedAt"
              defaultMessage="Last modified at {date} by {user}"
              description="Text for last modified date and user. Example: date = 'July 15, 2024', user = 'John Smith'"
              values={{
                date: formatDate(item.date_modified, 'short_datetime'),
                user: item.last_modified_by,
              }}
            />
          </span>

          <span className={classes.fieldDescripContainer}>
            {item.description}
          </span>
          <span className={classes.attachFileContainer}>
            <input
              aria-hidden={true}
              tabIndex="-1"
              type="file"
              ref="fileContainer"
              id="file"
              data-testid="version-file-replace"
              name="file"
              accept="*"
              onChange={this.handleFileAttachment}
            />
          </span>
          <button
            className={classes.fieldViewContainer}
            onClick={() => handleShowModal(MODAL_VERSION_DOCUMENT_VIEWER, item)}
          >
            {intl.formatMessage({
              id: 'EcVersionItem.viewButton',
              defaultMessage: 'View',
              description: 'Button text to view version',
            })}
          </button>
          <span>{this.renderExtraOptions(item)}</span>
        </div>
        {expanded ? this.renderDetail(item) : null}
      </div>
    );
  }
}

EcVersionItem.propTypes = {
  classes: PropTypes.object.isRequired,
  item: PropTypes.object.isRequired,
  handleShowModal: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
};

export default injectIntl(
  injectSheet(styles)(
    withFormatDate(withCurrentUser(withFlags(EcVersionItem))),
  ),
);
